import { ArrowLeftOutlined, ExportOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Descriptions, Empty, Spin, Typography } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { vehiclesApi } from "./vehicles-api";

export function VehicleSpecificationsPage() {
  const { id = "" } = useParams();
  const { text } = useAppConfig();
  const navigate = useNavigate();
  const vehicle = useQuery({ queryKey: ["vehicle", id], queryFn: () => vehiclesApi.get(id) });
  const decoded = useQuery({
    queryKey: ["vin-decode", vehicle.data?.vin],
    queryFn: () => vehiclesApi.decodeVin(vehicle.data!.vin),
    enabled: Boolean(vehicle.data?.vin),
    staleTime: 24 * 60 * 60 * 1_000
  });

  if (vehicle.isLoading) return <Spin />;
  if (!vehicle.data) return <Empty />;
  const data = decoded.data;
  const items = data ? [
    [text.modelYear, data.year],
    [text.make, data.make],
    [text.model, data.model],
    [text.trim, data.trim],
    [text.series, data.series],
    [text.bodyClass, data.bodyClass],
    [text.vehicleType, data.vehicleType],
    [text.manufacturer, data.manufacturer],
    [text.engine, [data.engineModel, data.engineCylinders && `${data.engineCylinders} cyl`, data.displacementL && `${data.displacementL} L`].filter(Boolean).join(" · ")],
    [text.fuelType, data.fuelType],
    [text.transmission, [data.transmission, data.transmissionSpeeds && `${data.transmissionSpeeds} speed`].filter(Boolean).join(" · ")],
    [text.driveType, data.driveType],
    [text.doors, data.doors],
    [text.gvwr, data.gvwr],
    [text.brakeSystem, data.brakeSystem],
    ["ABS", data.abs],
    ["ESC", data.esc],
    [text.tractionControl, data.tractionControl],
    ["TPMS", data.tpms],
    [text.manufacturingPlant, [data.plantCity, data.plantState, data.plantCountry].filter(Boolean).join(", ")]
  ].filter(([, value]) => value) : [];

  return <section className="vehicle-specifications-page">
    <div className="page-title business-page-title">
      <div>
        <Typography.Text type="secondary">{text.vehicleConfiguration}</Typography.Text>
        <Typography.Title level={2}>{vehicle.data.year} {vehicle.data.make} {vehicle.data.model}</Typography.Title>
        <Typography.Text className="mono">{vehicle.data.vin}</Typography.Text>
      </div>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/vehicles/${id}`)}>{text.backToVehicle}</Button>
    </div>

    {decoded.isError && <Alert type="warning" showIcon message={text.vinDecodeUnavailable} />}
    <Card
      title={text.nhtsaVehicleConfiguration}
      extra={<Button
        icon={<ExportOutlined />}
        href={`https://vpic.nhtsa.dot.gov/decoder/Decoder?VIN=${encodeURIComponent(vehicle.data.vin)}`}
        target="_blank"
        rel="noreferrer"
      >NHTSA</Button>}
    >
      {decoded.isLoading ? <Spin /> : items.length ? <Descriptions
        bordered
        column={{ xs: 1, md: 2 }}
        items={items.map(([label, value], index) => ({ key: index, label, children: value || "—" }))}
      /> : <Empty description={text.noDecodedSpecifications} />}
      {data?.errorText && data.errorCode !== "0" && <Alert className="vin-decode-note" type="info" showIcon message={data.errorText} />}
    </Card>
  </section>;
}
